/**
 * Hook that builds rich page context for the agent.
 *
 * Uses a global store that page components populate with their loaded data.
 * This avoids redundant API calls — the agent gets project/document/column
 * info that's already on screen.
 *
 * Components call usePageContextUpdater() to push data when it loads.
 * ChatWidget calls usePageContext() to read it on every message.
 */
import { useLocation } from 'react-router-dom';
import { useEffect, useCallback, useRef } from 'react';
import { PageContext } from '../services/agentApi';

// Extend PageContext with rich data the agent can use without extra API calls
export interface RichPageContext extends PageContext {
  projectName?: string;
  documents?: Array<{ documentId: string; fileName: string; status: string }>;
  columns?: Array<{ columnId: string; name: string; type: string }>;
  analysisRunId?: string;
}

// Global store — survives re-renders, cleared on navigation
const _store: { current: Partial<RichPageContext> } = { current: {} };

/**
 * Called by page components (ProjectDetail, ProjectsList) to push
 * their loaded data into the context store.
 */
export const setPageContextData = (data: Partial<RichPageContext>) => {
  _store.current = { ..._store.current, ...data };
};

/**
 * Clear the store — called on route changes.
 */
export const clearPageContextData = () => {
  _store.current = {};
};

/**
 * Hook for ChatWidget to read the current page context.
 *
 * Note: useParams() is NOT used here because ChatWidget is rendered outside
 * the <Routes> tree (sibling to AppRouter in App.tsx), so route params are
 * unavailable. Instead, page components push projectId via usePageContextUpdater().
 */
export const usePageContext = (): RichPageContext => {
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);

  // Clear store when pathname actually changes
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      clearPageContextData();
      prevPathRef.current = location.pathname;
    }
  }, [location.pathname]);

  // Derive currentPage from pathname + store data
  const projectId = _store.current.projectId;
  const isProjectDetail = location.pathname.startsWith('/projects/') && projectId;

  return {
    currentPage: isProjectDetail
      ? 'project-detail'
      : location.pathname === '/projects'
        ? 'projects-list'
        : location.pathname.replace('/', ''),
    projectId,
    ..._store.current,
  };
};

/**
 * Hook for page components to push their data into the context store.
 * Call this in ProjectDetail after data loads.
 *
 * Usage:
 *   const pushContext = usePageContextUpdater();
 *   useEffect(() => {
 *     if (project) pushContext({ projectName: project.name, ... });
 *   }, [project]);
 */
export const usePageContextUpdater = () => {
  return useCallback((data: Partial<RichPageContext>) => {
    setPageContextData(data);
  }, []);
};
